import startCase from 'lodash/startCase';
import { defineStore } from 'pinia';
import { io } from 'socket.io-client';
import { ref, watch } from 'vue';

import { useConstellationGenerationStore } from '@/app/constellationPro/stores/constellation-generation';
import { autoPublishErrors, PLATFORMS, postStatus } from '@/app/scheduler/constants';
import { allPosts } from '@/app/scheduler/utils/post-query';
import { env } from '@/env';
import { USER } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useBulkDownloadStore } from '@/stores/bulk-download';
import { useCampaignsStore } from '@/stores/campaigns';
import { useCsvStore } from '@/stores/csv';
import { useDashboardsStore } from '@/stores/dashboards';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useNotificationStore } from '@/stores/notification';
import { useOutageBannersStore } from '@/stores/outage-banners';
import { usePdfStore } from '@/stores/pdf';
import { usePlatformStore } from '@/stores/platform';
import { useSchedulerStore } from '@/stores/scheduler';
import { useSchedulerPinterestStore } from '@/stores/scheduler-pinterest';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useTokenStore } from '@/stores/token';
import { useXlsxStore } from '@/stores/xlsx';
import { downloadFileFromUrl } from '@/utils';
import { logger } from '@/utils/logger';

const SOCKETIO_EVENT = {
  CONNECT: 'connect',
  RECONNECT: 'reconnect',
};

export const EVENT = {
  REGISTER: 'register',
  REGISTER_USER: 'register_user',
  JOIN_BRAND_ROOMS: 'join_brand_rooms',
  NOTIFICATION: 'notification',
  PDF_GENERATED: 'pdf_generated',
  CSV_GENERATED: 'csv_generated',
  XLSX_GENERATED: 'xlsx_generated',
  SCHEDULED_POST_CALLBACK: 'scheduled_post_callback',
  SCHEDULED_POST_UPDATED: 'scheduled_post_updated',
  CLUSTER_TASK_NOTIFICATION: 'CLUSTER_TASK_NOTIFICATION',
  SYSTEM_REFRESH_BANNER: 'system_refresh_banner',
  VIDEO_GENERATED: 'video_generated',
  IMAGE_EDITED: 'image_edited',
  MEDIA_ALTERNATIVES_GENERATED: 'media_alternatives_generated',
  AD_ALTERNATIVES_GENERATED: 'ad_alternatives_generated',
  AI_GENERATION_BATCH_COMPLETED: 'ai_generation_batch_completed',
  MEDIA_BULK_DOWNLOAD_GENERATED: 'media_bulk_download_generated',
  MEDIA_BULK_DOWNLOAD_FAILED: 'media_bulk_download_failed',
  PRODUCT_BULK_IMPORT_COMPLETED: 'product_bulk_import_completed',
  PRODUCT_BULK_IMPORT_FAILED: 'product_bulk_import_failed',
};

export const useSocketStore = defineStore('socket', () => {
  const authStore = useAuthStore();
  const tokenStore = useTokenStore();
  const notificationStore = useNotificationStore();
  const pdfStore = usePdfStore();
  const csvStore = useCsvStore();
  const xlsxStore = useXlsxStore();
  const dashboardsStore = useDashboardsStore();
  const campaignsStore = useCampaignsStore();
  const platformStore = usePlatformStore();
  const schedulerStore = useSchedulerStore();
  const schedulerPinterestStore = useSchedulerPinterestStore();
  const socialListeningStore = useSocialListeningStore();
  const impersonatorStore = useImpersonatorStore();
  const outageBannersStore = useOutageBannersStore();
  const constellationGenerationStore = useConstellationGenerationStore();
  const bulkDownloadStore = useBulkDownloadStore();

  let socket = null;
  const id = ref();
  const productBulkImportResult = ref(null);

  const schedulerCallbackData = ref(null);

  function onNotification(data) {
    notificationStore.setToast({
      message: data.message,
      type: data.level,
    });
  }

  async function onPdfGenerated(data) {
    const meta = data?.meta ? JSON.parse(data.meta) : undefined;
    if (meta?.type) {
      pdfStore.receiveReport(meta.type);
    }
    if (data?.pdf_failed) {
      const type = meta?.type || '';
      notificationStore.setToast({
        message: `Failed to generate ${startCase(type)} PDF, please try again later.`,
        type: 'error',
      });
    } else if (data?.url) {
      downloadFileFromUrl(data.url);
    }
  }

  async function onCsvGenerated(data) {
    const { type } = data;
    const platformCsvList = [
      'community',
      'contentrights',
      'email',
      'facebook',
      'gallery',
      'instagram',
      'linkedin',
      'pinterest',
      'relationship',
      'snapchat',
      'tiktok',
      'threads',
      'twitter',
      'youtube',
      'creator',
    ];

    if (platformCsvList.some((platform) => type.toLowerCase().startsWith(platform))) {
      csvStore.csvReceived({ type });
    } else if (type.startsWith('auth')) {
      authStore.receivedCSV();
    }
    downloadFileFromUrl(data.url);
  }

  async function onXlsxGenerated(data) {
    const { type } = data;

    const platformXlsxList = ['creator'];

    if (platformXlsxList.some((platform) => type.toLowerCase().startsWith(platform))) {
      xlsxStore.xlsxReceived({ type });
    } else if (type.startsWith('dashboard')) {
      dashboardsStore.setDownloadingSpreadsheetExport(false);
    } else if (type.startsWith('campaign')) {
      campaignsStore.setDownloadingSpreadsheet(false);
    }

    if (data?.error) {
      notificationStore.setToast({
        message: `Failed to generate ${startCase(type)} spreadsheet, please try again later.`,
        type: 'error',
      });
    } else if (data?.url) {
      downloadFileFromUrl(data.url);
    }
  }

  async function handlePinterestAutopublish(data, post, postBrand, result) {
    let pinterestUsername = platformStore.pinterestAccount?.pinterestUsername;
    if (
      !platformStore.pinterestAccount ||
      platformStore.pinterestAccount.brandId !== postBrand.id
    ) {
      await schedulerPinterestStore.getPinterestAccounts({ brandIds: [postBrand.id.toString()] });
      pinterestUsername = schedulerPinterestStore.pinterestAccounts[0].pinterest_username;
      schedulerPinterestStore.clearPinterestAccounts();
    }

    schedulerStore.updatePendingPostStatus({ id: post.id, result });

    const publishingPinterestPosts = allPosts
      .forBrand(authStore.currentBrand?.id)
      .forPlatform(PLATFORMS.PINTEREST)
      .withStatuses([postStatus.AUTOPUBLISHING]);

    if (schedulerStore.getPosts(publishingPinterestPosts).length > 0) return;

    const pendingPostDict = schedulerStore.pendingPostDict;
    const successCount = Object.values(pendingPostDict).filter((s) => s === 'SUCCESS').length;
    const failCount = Object.values(pendingPostDict).filter((s) => s === 'FAILURE').length;

    schedulerStore.clearPendingPostDict();

    schedulerCallbackData.value = {
      type: 'pinterest',
      data,
      pinterestUsername,
      successCount,
      failCount,
    };
  }

  async function onScheduledPostCallback(data) {
    if (!authStore.identity) {
      return;
    }

    // Web doesnt support toast for reminders of non-autopublish scheduled posts.
    // Ignore these callbacks for now
    if (data.post_status) {
      return;
    }
    const { platform, post, result } = data;

    const postBrand = Object.values(authStore.identity.brands).find((b) => b.id === post.brand_id);
    if (
      post.error &&
      [autoPublishErrors.MISSING_AUTH, autoPublishErrors.INVALID_AUTH].includes(post.error)
    ) {
      platformStore.getPlatformConnections();
    }

    schedulerStore.replacePost({ ...post, platform });

    if (platform === 'pinterest') {
      await handlePinterestAutopublish(data, post, postBrand, result);
    } else {
      schedulerCallbackData.value = {
        platform,
        data,
        brandName: postBrand.name,
      };
    }
  }

  async function onScheduledPostUpdated(data) {
    const { platform, post, result } = data;

    if (!post || !authStore.currentBrand?.id) {
      return;
    }

    if (post.brand_id !== authStore.currentBrand.id) {
      return;
    }

    if (result === 'UPDATED' || result === 'CREATED') {
      // Re-fetch via the API instead of using the websocket's data for Instagram updates.
      // This solves a problem with fields (user/product tags) that use separate tables, which
      // causes inconsistent behaviour when the ORM hooks are triggered and the websockets sent.
      if (platform === 'instagram' && result === 'UPDATED') {
        await schedulerStore.fetchPost({ platform, ...post });
      } else {
        schedulerStore.replacePost({ platform, ...post });
      }
    } else if (result === 'DELETED') {
      schedulerStore.removePost({ platform, ...post });
    }
  }

  function onSystemRefreshBanner() {
    outageBannersStore.loadBannersFromCDN();
  }

  function onMediaBulkDownloadGenerated(data) {
    const taskId = data?.task_id ?? data?.taskId;
    const url = data?.url ?? data?.download_url ?? data?.downloadUrl;
    if (taskId == null || !bulkDownloadStore.isActiveTask(String(taskId))) return;
    bulkDownloadStore.clearTask(String(taskId));
    if (url) {
      downloadFileFromUrl(url);
    } else {
      logger.warn('[bulk-download] success event missing url', { data });
    }
  }

  function onMediaBulkDownloadFailed(data) {
    const taskId = data?.task_id ?? data?.taskId;
    if (taskId == null || !bulkDownloadStore.isActiveTask(String(taskId))) return;
    bulkDownloadStore.clearTask(String(taskId));
    notificationStore.setToast({
      message: data?.error ?? 'Bulk download failed. Please try again.',
      type: 'error',
    });
  }

  function onProductBulkImportCompleted(data) {
    const successCount = data?.success_count ?? 0;
    const errorCount = data?.error_count ?? 0;
    if (errorCount > 0) {
      logger.warn('[product-bulk-import] completed with errors', { errors: data?.errors });
    }
    productBulkImportResult.value = { status: 'completed', successCount, errorCount };
  }

  function onProductBulkImportFailed(data) {
    logger.warn('[product-bulk-import] import failed', { error: data?.error });
    productBulkImportResult.value = { status: 'failed' };
  }

  function onAiGenerationBatchCompleted(data) {
    const {
      batch_id: batchId,
      gallery_id: galleryId,
      succeeded,
      total_jobs: totalJobs,
      failed,
    } = data ?? {};
    if (batchId == null || galleryId == null) return;
    const payload = { galleryId, succeeded, totalJobs, failed };
    if (constellationGenerationStore.activeBatches[batchId]) {
      constellationGenerationStore.completeBatch(batchId, payload);
      return;
    }
    if (constellationGenerationStore.isDiscovering) {
      constellationGenerationStore.bufferCompletion(batchId, payload);
      return;
    }
    logger.warn(
      `Ignoring ai_generation_batch_completed for batch ${batchId} (gallery ${galleryId}) — not in this session's known active batches (phantom event, or completion arrived before rehydration).`,
    );
  }

  function registerGlobalListeners() {
    socket.on(EVENT.NOTIFICATION, onNotification);
    socket.on(EVENT.PDF_GENERATED, onPdfGenerated);
    socket.on(EVENT.CSV_GENERATED, onCsvGenerated);
    socket.on(EVENT.XLSX_GENERATED, onXlsxGenerated);
    socket.on(EVENT.SCHEDULED_POST_CALLBACK, onScheduledPostCallback);
    socket.on(EVENT.SCHEDULED_POST_UPDATED, onScheduledPostUpdated);
    socket.on(EVENT.CLUSTER_TASK_NOTIFICATION, socialListeningStore.onClusterTaskNotification);
    socket.on(EVENT.SYSTEM_REFRESH_BANNER, onSystemRefreshBanner);
    socket.on(EVENT.AI_GENERATION_BATCH_COMPLETED, onAiGenerationBatchCompleted);
    socket.on(EVENT.MEDIA_BULK_DOWNLOAD_GENERATED, onMediaBulkDownloadGenerated);
    socket.on(EVENT.MEDIA_BULK_DOWNLOAD_FAILED, onMediaBulkDownloadFailed);
    socket.on(EVENT.PRODUCT_BULK_IMPORT_COMPLETED, onProductBulkImportCompleted);
    socket.on(EVENT.PRODUCT_BULK_IMPORT_FAILED, onProductBulkImportFailed);
    // Ensure banners are up to date if socket was down for a period of time
    socket.io.on(SOCKETIO_EVENT.RECONNECT, onSystemRefreshBanner);
  }

  function registerIdentity() {
    const isLoggedIn = authStore.isLoggedIn;
    const hasToken = !!tokenStore.token;
    const notImpersonating = !impersonatorStore.isImpersonating;

    if (isLoggedIn && hasToken && socket?.connected) {
      socket.emit(EVENT.REGISTER, tokenStore.token);

      const canAccessCommunity = authStore.guard(USER.COMMUNITY.CAN_ACCESS_COMMUNITY);
      const canAccessScheduler = authStore.guard(USER.SCHEDULER.CAN_ACCESS_SCHEDULER);
      const canAccessCreators = authStore.guard(USER.CREATORS.CAN_ACCESS_CREATORS);
      if (notImpersonating && (canAccessScheduler || canAccessCommunity || canAccessCreators)) {
        socket.emit(EVENT.JOIN_BRAND_ROOMS, tokenStore.token);
      }
    }
  }

  function disconnect() {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
  }

  function init() {
    if (socket?.connected) {
      disconnect();
    }

    const extraHeaders = {};
    if (impersonatorStore.isImpersonating) {
      extraHeaders['X-On-Behalf-Of'] = impersonatorStore.impersonatedUserId;
    }

    socket = io(env.notificationServiceUrl, { extraHeaders });
    socket.on(SOCKETIO_EVENT.CONNECT, () => {
      id.value = socket.id;
      registerIdentity();
    });
    socket.io.on(SOCKETIO_EVENT.RECONNECT, () => {
      id.value = socket.id;
      registerIdentity();
    });
    socket.on('disconnect', (reason, details) => {
      logger.debug(`socket io disconnected, reason: ${reason}`);
      if (details) {
        logger.debug(
          `socket io disconnected, details:
          message=${details.message}
          description=${details.description}
          context=${details.context}`,
        );
      }
      if (reason === 'io server disconnect') {
        logger.warn('socket io server disconnected. Trying to reconnect.');
        // the disconnection was initiated by the server, reconnect manually
        socket.connect();
      }
    });
    registerGlobalListeners();
  }

  function addListener(event, callback) {
    if (socket) {
      socket.on(event, callback);
    }
  }

  function removeListener(event, callback) {
    if (socket) {
      socket.off(event, callback);
    }
  }

  /**
   * Register a one-shot listener that auto-removes after the first invocation.
   * Returns a cleanup function that removes the listener if it hasn't fired yet.
   *
   * When a `filter` function is provided, non-matching payloads are ignored
   * and the listener stays active until a matching payload arrives or cleanup
   * is called.
   */
  function addOnceListener(event, callback, filter) {
    if (!socket) return () => {};

    let handler;
    if (filter) {
      handler = (payload) => {
        if (!filter(payload)) return;
        socket?.off(event, handler);
        callback(payload);
      };
      socket.on(event, handler);
    } else {
      handler = (payload) => {
        callback(payload);
      };
      socket.once(event, handler);
    }

    return () => {
      socket?.off(event, handler);
    };
  }

  /**
   * Simulate an incoming socket event in Cypress e2e tests.
   * Triggers all registered listeners (including addOnceListener) as if
   * the server had pushed the event — without any real server round-trip.
   */
  function simulateIncomingEvent(event, payload) {
    if (!socket) return;
    socket
      .listeners(event)
      .slice(0)
      .forEach((fn) => fn.call(socket, payload));
  }

  function hasIncomingEventListener(event) {
    return (socket?.listeners(event)?.length ?? 0) > 0;
  }

  watch(
    () => authStore.isLoggedIn,
    (newIsLoggedIn, oldIsLoggedIn) => {
      if (newIsLoggedIn === oldIsLoggedIn) {
        return;
      }
      if (newIsLoggedIn) {
        init();
      } else {
        disconnect();
      }
    },
    { immediate: true },
  );

  // Expose socket event simulation for Cypress e2e tests
  if (window.Cypress) {
    window.__cypressEmitSocketEvent__ = simulateIncomingEvent;
    window.__cypressHasSocketListener__ = hasIncomingEventListener;
  }

  return {
    id,
    init,
    addListener,
    addOnceListener,
    removeListener,
    registerIdentity,
    onPdfGenerated,
    onNotification,
    onCsvGenerated,
    onXlsxGenerated,
    onScheduledPostUpdated,
    onAiGenerationBatchCompleted,
    onMediaBulkDownloadGenerated,
    onMediaBulkDownloadFailed,
    onProductBulkImportCompleted,
    onProductBulkImportFailed,
    productBulkImportResult,
    schedulerCallbackData,
  };
});
